import type { ApprovalAttributes } from '@/types/approval';
import {
  SERP_NOT_LOADED_MESSAGE,
  type SerpDocumentRef,
} from '@/utils/approval/serpDocument';

/** SERP 매장반품 요청 — 레거시 appr_edit_forms_serp_shop_return */
export const SERP_STORE_RETURN_TYPE = 171;

export const isSerpStoreReturnType = (apvType: number): boolean =>
  apvType === SERP_STORE_RETURN_TYPE;

export type SerpStoreReturnItem = {
  itmCd?: string | null;
  itmNm?: string | null;
  ssnCd?: string | null;
  prodCd?: string | null;
  prodNm?: string | null;
  colorCd?: string | null;
  skdDe?: string | null;
  carryOver?: string | null;
};

export type SerpStoreReturnSummaryRow = {
  ssnCd: string;
  prodCnt: string;
  colorCnt: string;
};

/** EAI 반품 상세 원문(draft-source display.source). 저장 data 는 여기에 summaryList 를 더한 것 */
export type SerpStoreReturnSource = {
  brandCd?: string | null;
  distTypNm?: string | null;
  operSsnCd?: string | null;
  rtnDeg?: number | string | null;
  rtnReqNo?: string | null;
  tit?: string | null;
  staDe?: string | null;
  endDe?: string | null;
  boxKepYn?: string | null;
  /** 스웨거 표기. 실제 EAI 응답·레거시 화면은 boxKepYn */
  boxKeyYn?: string | null;
  prodList?: SerpStoreReturnItem[] | null;
  summaryList?: SerpStoreReturnSummaryRow[] | null;
  [key: string]: unknown;
};

export type SerpStoreReturnDraftSource = {
  code: number;
  attributes: SerpDocumentRef;
  display: { source?: SerpStoreReturnSource | null };
};

export type SerpStoreReturnState = SerpDocumentRef & {
  source: SerpStoreReturnSource;
};

export const getSerpReturnBoxKeepYn = (source: SerpStoreReturnSource) =>
  source.boxKepYn ?? source.boxKeyYn ?? null;

export const getSerpReturnItems = (
  source: SerpStoreReturnSource,
): SerpStoreReturnItem[] =>
  Array.isArray(source.prodList)
    ? source.prodList.filter(
        (row): row is SerpStoreReturnItem => !!row && typeof row === 'object',
      )
    : [];

/**
 * 시즌별 스타일·컬러 수 — draft-source 가 SERP 요약(dftSummary)을 주지 않아 상세로 셈.
 * 스타일=prodCd, 컬러=스타일+컬러 조합. 레거시 저장 요약과 같은 결과(dev 문서 대조)
 */
export const buildSerpReturnSummary = (
  items: SerpStoreReturnItem[],
): SerpStoreReturnSummaryRow[] => {
  const seasons = new Map<
    string,
    { styles: Set<string>; colors: Set<string> }
  >();
  items.forEach((item) => {
    const season = item.ssnCd ?? '';
    const entry = seasons.get(season) ?? {
      styles: new Set<string>(),
      colors: new Set<string>(),
    };
    entry.styles.add(item.prodCd ?? '');
    entry.colors.add(`${item.prodCd ?? ''}|${item.colorCd ?? ''}`);
    seasons.set(season, entry);
  });
  return Array.from(seasons, ([ssnCd, { styles, colors }]) => ({
    ssnCd,
    prodCnt: String(styles.size),
    colorCnt: String(colors.size),
  }));
};

/** 저장 data 에 상세(prodList)가 있으면 그 문서의 작성 시점 상세. 레거시 요약만 저장된 문서는 null */
export const parseSavedSerpReturnDetail = (
  attributes: unknown,
): SerpStoreReturnSource | null => {
  if (!attributes || typeof attributes !== 'object') return null;
  const data = (attributes as Record<string, unknown>).data;
  if (!data || typeof data !== 'object') return null;
  const source = data as SerpStoreReturnSource;
  return Array.isArray(source.prodList) ? source : null;
};

export const checkSerpStoreReturnBeforeSave = (
  state: SerpStoreReturnState | null | undefined,
): string | null => (state?.source ? null : SERP_NOT_LOADED_MESSAGE);

/** data = 레거시와 같은 요약(헤더+summaryList) + 조회 화면 「자세히 보기」용 작성 시점 상세(prodList) */
export const buildSerpStoreReturnAttributes = (
  state: SerpStoreReturnState | null | undefined,
  content: string,
): ApprovalAttributes | null =>
  state?.source
    ? {
        typeCode: state.typeCode,
        sequence: state.sequence,
        content,
        data: {
          ...state.source,
          summaryList: buildSerpReturnSummary(getSerpReturnItems(state.source)),
        },
      }
    : null;
